import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoNotificationsOutline, IoCheckmarkDoneOutline, IoTrashOutline, IoChatbubbleOutline, IoAtOutline, IoHeartOutline, IoPersonAddOutline } from 'react-icons/io5';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import Avatar from '../components/Avatar';
import { notificationApi } from '../services/notificationApi';
import './Notifications.css';

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const limit = 50;

  const loadNotifications = useCallback(async (reset = true) => {
    try {
      setLoading(true);
      const offset = reset ? 0 : notifications.length;
      const response = await notificationApi.getNotifications(limit, offset);
      if (response.success) {
        if (reset) {
          setNotifications(response.data || []);
        } else {
          setNotifications(prev => [...prev, ...(response.data || [])]);
        }
        setHasMore(response.data?.length >= limit);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [notifications.length]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    await loadNotifications(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      try {
        await notificationApi.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate based on notification type
    let link = '/';
    switch (notification.type) {
      case 'tag_post':
      case 'reply_post':
      case 'reply_post_tagged':
        if (notification.post_id) {
          link = `/post/${notification.post_id}`;
        }
        break;
      case 'tag_comment':
      case 'reply_comment':
      case 'reply_comment_tagged':
        if (notification.post_id) {
          link = `/post/${notification.post_id}`;
          if (notification.comment_id) {
            link += `?highlight_comment=${notification.comment_id}`;
          }
        }
        break;
      case 'friend_request':
      case 'friend_accepted':
        link = '/friends';
        break;
      default:
        link = '/';
    }

    navigate(link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'tag_post':
      case 'tag_comment':
        return <IoAtOutline className="notification-type-icon" />;
      case 'reply_post':
      case 'reply_comment':
      case 'reply_post_tagged':
      case 'reply_comment_tagged':
        return <IoChatbubbleOutline className="notification-type-icon" />;
      case 'friend_request':
      case 'friend_accepted':
        return <IoPersonAddOutline className="notification-type-icon" />;
      default:
        return <IoNotificationsOutline className="notification-type-icon" />;
    }
  };

  const getNotificationText = (notification) => {
    const fromName = notification.from_user?.display_name || 'Someone';
    switch (notification.type) {
      case 'tag_post':
        return t('notifications.taggedYouPost') || 'tagged you in a post';
      case 'tag_comment':
        return t('notifications.taggedYouComment') || 'tagged you in a comment';
      case 'reply_post':
        return t('notifications.repliedToYourPost') || 'replied to your post';
      case 'reply_comment':
        return t('notifications.repliedToYourComment') || 'replied to your comment';
      case 'reply_post_tagged':
        return t('notifications.repliedToPostYoureTagged') || 'replied to a post you\'re tagged in';
      case 'reply_comment_tagged':
        return t('notifications.repliedToCommentYoureTagged') || 'replied to a comment you\'re tagged in';
      case 'friend_request':
        return t('notifications.sentFriendRequest') || 'sent you a friend request';
      case 'friend_accepted':
        return t('notifications.acceptedFriendRequest') || 'accepted your friend request';
      default:
        return notification.content || 'New notification';
    }
  };

  const locale = i18n.language === 'vi' ? vi : enUS;

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>{t('notifications.title') || 'Notifications'}</h1>
          <button
            className="mark-all-read-btn"
            onClick={handleMarkAllRead}
          >
            <IoCheckmarkDoneOutline />
            {t('notifications.markAllRead') || 'Mark all as read'}
          </button>
        </div>

        {loading ? (
          <div className="notifications-loading">
            {t('common.loading') || 'Loading...'}
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <IoNotificationsOutline className="empty-icon" />
            <p>{t('notifications.noNotifications') || 'No notifications yet'}</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon-wrapper">
                    {notification.from_user ? (
                      <Avatar user={notification.from_user} size="medium" />
                    ) : (
                      <div className="notification-default-avatar">
                        <IoNotificationsOutline />
                      </div>
                    )}
                    <div className="notification-type-badge">
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>

                  <div className="notification-content">
                    <p className="notification-text">
                      {notification.from_user && (
                        <strong>{notification.from_user.display_name}</strong>
                      )}
                      {' '}{getNotificationText(notification)}
                    </p>
                    <span className="notification-time">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale })}
                    </span>
                  </div>

                  <div className="notification-actions">
                    {!notification.is_read && (
                      <div className="unread-dot" />
                    )}
                    <button
                      className="notification-delete"
                      onClick={(e) => handleDelete(e, notification.id)}
                      title={t('common.delete') || 'Delete'}
                    >
                      <IoTrashOutline />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="notifications-load-more">
                <button
                  className="load-more-btn"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore
                    ? (t('common.loading') || 'Loading...')
                    : (t('notifications.loadMore') || 'Load more')
                  }
                </button>
              </div>
            )}

            {!hasMore && notifications.length > 0 && (
              <div className="notifications-end">
                <p>{t('notifications.noMore') || 'No more notifications'}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
